import { useLazyQuery } from '@apollo/client/react'

import { graphql } from '~publish/graphql'

export const ValidateAPIClientName = graphql(/* GraphQL */ `
  query validateAPIClientName($input: ValidateAPIClientNameInput!) {
    validateAPIClientName(input: $input) {
      __typename
      ... on ValidateAPIClientNameSuccess {
        isValid
      }
      ... on InvalidInputError {
        message
      }
    }
  }
`)

const VALIDATION_REQUEST_ERROR =
  "We couldn't validate this client name. Please try again."

interface UseValidateAPIClientNameReturn {
  validate: (clientName: string, signal?: AbortSignal) => Promise<string | null>
}

export function useValidateAPIClientName(): UseValidateAPIClientNameReturn {
  const [executeQuery] = useLazyQuery(ValidateAPIClientName, {
    fetchPolicy: 'network-only',
  })

  const validate = async (
    clientName: string,
    signal?: AbortSignal,
  ): Promise<string | null> => {
    try {
      const { data, error } = await executeQuery({
        variables: { input: { clientName } },
        context: {
          queryDeduplication: false,
          fetchOptions: { signal },
        },
      })

      if (signal?.aborted) {
        return null
      }

      if (error) return VALIDATION_REQUEST_ERROR

      const result = data?.validateAPIClientName
      if (result?.__typename === 'InvalidInputError') {
        return result.message
      }

      if (result?.__typename === 'ValidateAPIClientNameSuccess') {
        return result.isValid ? null : 'This client name is unavailable.'
      }

      return VALIDATION_REQUEST_ERROR
    } catch {
      return signal?.aborted ? null : VALIDATION_REQUEST_ERROR
    }
  }

  return { validate }
}
